/**
 * DashboardDataContext — F1: Dashboard Shared Data Layer
 *
 * Fetches pre-aggregated data from dedicated dashboard endpoints. Widgets
 * consume this context rather than making their own independent API calls.
 *
 * PRD §7.2 — replaces the bulk `fetchProjects('dashboard', …, { statusId: 2 })`
 * call (~3,700 full Project objects) with ~8-10 targeted, pre-aggregated
 * endpoint calls that fire in parallel.
 *
 * Usage:
 *   Wrap the DashboardPage with <DashboardDataProvider>.
 *   Widgets call useDashboardData() to access shared state.
 */

import React, { createContext, useContext, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import type { Project, WIPReport, UtilisationData, WipAgeBand } from '@/types';
import type { RevenueRecognition } from '@/types/budget.types';
import {
  fetchProjects,
  fetchAllWIPReports,
  fetchUtilisationData,
  fetchDashboardWipSummary,
  fetchUtilisationSummary,
  fetchExecutiveKpis,
  fetchFinanceKpis,
  fetchDashboardAtRiskSummary,
  fetchDashboardRevenueRecognitionSummary,
  fetchClientConcentration,
  fetchPortfolioProfitability,
  fetchUpcomingKeyDates,
  fetchRevenueCostBurnByProject,
  fetchBillingPipeline,
  fetchDashboardOverview,

  getDataMode,
} from '@/lib/data-service';
import type {
  DashboardOverviewBundle,
  DashboardOverviewPersona,
  DashboardOverviewSection,
  ExecutiveKpis,
  FinanceKpis,
  DashboardAtRiskSummary,
  DashboardRevenueRecognitionSummary,
  ClientConcentrationData,
  PortfolioProfitabilityData,
  KeyDateEvent,
  RevenueCostBurnProject,
  ProjectFinancialSummaryRow,
  BillingPipeline,
  DashboardWipSummary,
  UtilisationSummaryData,
} from '@/lib/data-service';
import { getBudgetDataByProjectId } from '@/lib/mock-data/budget-mock-data';
import { getWipThresholds } from '@/hooks/use-wip-thresholds';
import { useCompany } from '@/contexts/CompanyContext';
import { useAuth } from '@/providers/AuthProvider';
import { usePermissions } from '@/contexts/PermissionsContext';
import { resolvePersonaFromPermissions } from '@/contexts/DashboardPersonaContext';

/** Rows per page of the WIP Aging Overview table, paged server-side. */
export const DASHBOARD_WIP_PAGE_SIZE = 10;

/** Rows the billing and over/under-billing list widgets render. */
const DASHBOARD_WIP_ROW_LIMIT = 8;

/**
 * The bundle sections each persona's shell actually reads, so a persona does not pay for
 * widgets it never renders. The PM shell reads only the WIP figures from this context; its
 * other widgets have their own PM-scoped endpoints.
 */
const PERSONA_SECTIONS: Record<DashboardOverviewPersona, DashboardOverviewSection[]> = {
  executive: [
    'executiveKpis',
    'financeKpis',
    'atRiskProjects',
    'revenueRecognition',
    'portfolioProfitability',
    'upcomingKeyDates',
    'clientConcentration',
    'revenueCostBurnByProject',
    'utilisationSummary',
    'wipSummary',
  ],
  project_manager: ['wipSummary'],
  project_team: [],
};

const ALL_SECTIONS = PERSONA_SECTIONS.executive;
const INDEPENDENT_SECTIONS: ReadonlySet<DashboardOverviewSection> = new Set(['wipSummary']);

/** Thresholds the WIP summary's counts are computed against, server-side. */
export interface DashboardWipThresholds {
  underBillingThreshold: number;
  agedWipDaysThreshold: number;
}

// ── Per-project revenue recognition shape consumed by Finance widgets ─────────
export interface ProjectRevenueRecognition {
  projectId: number;
  projectName: string;
  clientName: string;
  method: RevenueRecognition['method'];
  budgetRevenue: number;
  completionPercent: number;
  revenueRecognised: number;
  revenueToRecognise: number;
  deferredRevenue: number;
  unbilledRevenue: number;
}

// ── Context shape ─────────────────────────────────────────────────────────────
export interface DashboardDataContextValue {
  // Pre-computed aggregates (replaces raw Project[])
  execKpis: ExecutiveKpis | null;
  finKpis: FinanceKpis | null;
  atRiskSummary: DashboardAtRiskSummary | null;
  /** Portfolio revenue-recognition figures, aggregated server-side. Null until it arrives. */
  revenueRecognitionSummary: DashboardRevenueRecognitionSummary | null;
  profitability: PortfolioProfitabilityData | null;
  keyDates: KeyDateEvent[];
  clientConcentration: ClientConcentrationData | null;
  revenueCostBurnByProject: RevenueCostBurnProject[];
  /** Server-aggregated invoice pipeline. Null outside live mode, where widgets derive it from mock data. */
  billingPipeline: BillingPipeline | null;

  // Lightweight per-project rows from SQL (live mode)
  projectFinancialSummary: ProjectFinancialSummaryRow[];

  /**
   * Live mode: every WIP figure the dashboard shows, from one call — full-set
   * totals, one server-paged page of the aged-WIP table, and the bounded lists.
   * Null in demo/tour, where widgets derive the same shapes from `wipReports`.
   */
  wipSummary: DashboardWipSummary | null;
  /** Re-pages/re-filters/re-thresholds the WIP summary server-side. Live mode only. */
  loadWipPage: (page: number, band: WipAgeBand | null, thresholds?: DashboardWipThresholds) => Promise<void>;
  wipPageLoading: boolean;

  /** Live mode: server-side utilisation aggregates and department breakdown. */
  utilisationSummary: UtilisationSummaryData | null;

  // Demo/tour mode only — mock rows, no HTTP
  wipReports: WIPReport[];
  utilisationData: UtilisationData[];

  // Backward compatibility: demo/tour mode still provides these
  projects: Project[];
  activeProjects: Project[];
  /** @deprecated Live mode reads `revenueRecognitionSummary`; these are demo/tour rows. */
  legacyRevenueRecognitions: ProjectRevenueRecognition[];

  // Granular loading states for progressive rendering
  kpisLoading: boolean;
  finKpisLoading: boolean;
  atRiskLoading: boolean;
  profitabilityLoading: boolean;
  revenueRecLoading: boolean;
  keyDatesLoading: boolean;
  clientConcLoading: boolean;
  rcbByProjectLoading: boolean;
  billingPipelineLoading: boolean;
  wipLoading: boolean;
  utilisationLoading: boolean;

  // Per-project data loading (live mode fetches projects in the background)
  projectsDataLoading: boolean;

  // Backward compatibility aliases
  projectsLoading: boolean;

  // Composite
  isLoading: boolean;
  isRefreshing: boolean;
  error: string | null;

  /** Manually trigger a full data refresh. */
  refresh: () => Promise<void>;

  /** Timestamp of the most recent successful fetch (ms since epoch). */
  lastFetchedAt: number | null;
}

const DashboardDataContext = createContext<DashboardDataContextValue | null>(null);

// ── Provider ──────────────────────────────────────────────────────────────────
export function DashboardDataProvider({ children }: { children: ReactNode }) {
  const { currentCompany } = useCompany();
  const { authenticatedUser } = useAuth();
  const { reports, isLoading: permissionsLoading } = usePermissions();

  // The same persona the shells are routed by, resolved from the same permissions — this
  // provider sits above DashboardPersonaProvider, so it resolves rather than consumes it.
  const persona: DashboardOverviewPersona = resolvePersonaFromPermissions(reports);

  // Derive managerId for PM-persona scoping: PMs who are NOT executives
  // should only see their own projects in revenue-recognition data.
  const revenueRecManagerId = (!reports.executiveDashboard && reports.pmDashboard && authenticatedUser?.id)
    ? parseInt(authenticatedUser.id, 10)
    : undefined;

  // Pre-computed aggregates from dedicated endpoints
  const [execKpis, setExecKpis] = useState<ExecutiveKpis | null>(null);
  const [finKpis, setFinKpis] = useState<FinanceKpis | null>(null);
  const [atRiskSummary, setAtRiskSummary] = useState<DashboardAtRiskSummary | null>(null);
  const [revenueRecognitionSummary, setRevenueRecognitionSummary] = useState<DashboardRevenueRecognitionSummary | null>(null);
  const [profitability, setProfitability] = useState<PortfolioProfitabilityData | null>(null);
  const [keyDates, setKeyDates] = useState<KeyDateEvent[]>([]);
  const [clientConcentration, setClientConcentration] = useState<ClientConcentrationData | null>(null);
  const [revenueCostBurnByProject, setRevenueCostBurnByProject] = useState<RevenueCostBurnProject[]>([]);
  const [billingPipeline, setBillingPipeline] = useState<BillingPipeline | null>(null);
  // projectFinancialSummary is now fetched on-demand per page by usePagedFinancialSummary hook.
  // Kept as empty array for backward-compatibility with any consumer that still reads it.
  const projectFinancialSummary: ProjectFinancialSummaryRow[] = [];

  // Live mode: aggregated + bounded + server-paged WIP; demo/tour: mock rows
  const [wipSummary, setWipSummary] = useState<DashboardWipSummary | null>(null);
  const [wipPageLoading, setWipPageLoading] = useState(false);
  const [utilisationSummary, setUtilisationSummary] = useState<UtilisationSummaryData | null>(null);
  const [wipReports, setWipReports] = useState<WIPReport[]>([]);
  const [utilisationData, setUtilisationData] = useState<UtilisationData[]>([]);

  // Backward compatibility: demo/tour mode project arrays
  const [projects, setProjects] = useState<Project[]>([]);
  const [legacyRevenueRecognitions, setLegacyRevenueRecognitions] = useState<ProjectRevenueRecognition[]>([]);

  // Granular loading states
  const [kpisLoading, setKpisLoading] = useState(true);
  const [finKpisLoading, setFinKpisLoading] = useState(true);
  const [atRiskLoading, setAtRiskLoading] = useState(true);
  const [profitabilityLoading, setProfitabilityLoading] = useState(true);
  const [revenueRecLoading, setRevenueRecLoading] = useState(true);
  const [keyDatesLoading, setKeyDatesLoading] = useState(true);
  const [clientConcLoading, setClientConcLoading] = useState(true);
  const [rcbByProjectLoading, setRcbByProjectLoading] = useState(true);
  const [billingPipelineLoading, setBillingPipelineLoading] = useState(true);
  const [wipLoading, setWipLoading] = useState(true);
  const [utilisationLoading, setUtilisationLoading] = useState(true);
  const [projectsDataLoading, setProjectsDataLoading] = useState(true);

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchedAt, setLastFetchedAt] = useState<number | null>(null);

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /**
   * How each widget's state is filled: from the bundle, or — when the bundle could not
   * produce that section — from the widget's own endpoint, which is what the widget used
   * before the bundle existed. `markSettled` drops the spinner for a widget this persona
   * does not render.
   */
  const buildSectionLoaders = (): Record<DashboardOverviewSection, {
    fromBundle: (bundle: DashboardOverviewBundle) => void;
    fetchOwn: () => Promise<void>;
    markSettled: () => void;
  }> => {
    const wipParams = {
      page: 1,
      pageSize: DASHBOARD_WIP_PAGE_SIZE,
      rowLimit: DASHBOARD_WIP_ROW_LIMIT,
      managerId: revenueRecManagerId,
      agedWipDaysThreshold: getWipThresholds().agedWipDaysThreshold,
    };

    return {
      executiveKpis: {
        fromBundle: b => { if (b.executiveKpis) setExecKpis(b.executiveKpis); setKpisLoading(false); },
        fetchOwn: () => fetchExecutiveKpis()
          .then(data => { if (isMountedRef.current) setExecKpis(data); })
          .catch(err => { console.error('[DashboardData] exec kpis:', err); })
          .finally(() => { if (isMountedRef.current) setKpisLoading(false); }),
        markSettled: () => setKpisLoading(false),
      },
      financeKpis: {
        fromBundle: b => { if (b.financeKpis) setFinKpis(b.financeKpis); setFinKpisLoading(false); },
        fetchOwn: () => fetchFinanceKpis()
          .then(data => { if (isMountedRef.current) setFinKpis(data); })
          .catch(err => { console.error('[DashboardData] fin kpis:', err); })
          .finally(() => { if (isMountedRef.current) setFinKpisLoading(false); }),
        markSettled: () => setFinKpisLoading(false),
      },
      atRiskProjects: {
        fromBundle: b => { setAtRiskSummary(b.atRiskProjects ?? null); setAtRiskLoading(false); },
        fetchOwn: () => fetchDashboardAtRiskSummary()
          .then(data => { if (isMountedRef.current) setAtRiskSummary(data); })
          .catch(err => { console.error('[DashboardData] at-risk:', err); })
          .finally(() => { if (isMountedRef.current) setAtRiskLoading(false); }),
        markSettled: () => setAtRiskLoading(false),
      },
      revenueRecognition: {
        fromBundle: b => { setRevenueRecognitionSummary(b.revenueRecognition ?? null); setRevenueRecLoading(false); },
        fetchOwn: () => fetchDashboardRevenueRecognitionSummary(revenueRecManagerId)
          .then(data => { if (isMountedRef.current) setRevenueRecognitionSummary(data); })
          .catch(err => { console.error('[DashboardData] rev rec:', err); })
          .finally(() => { if (isMountedRef.current) setRevenueRecLoading(false); }),
        markSettled: () => setRevenueRecLoading(false),
      },
      portfolioProfitability: {
        fromBundle: b => { setProfitability(b.portfolioProfitability ?? null); setProfitabilityLoading(false); },
        fetchOwn: () => fetchPortfolioProfitability()
          .then(data => { if (isMountedRef.current) setProfitability(data); })
          .catch(err => { console.error('[DashboardData] profitability:', err); })
          .finally(() => { if (isMountedRef.current) setProfitabilityLoading(false); }),
        markSettled: () => setProfitabilityLoading(false),
      },
      upcomingKeyDates: {
        fromBundle: b => { setKeyDates(b.upcomingKeyDates ?? []); setKeyDatesLoading(false); },
        fetchOwn: () => fetchUpcomingKeyDates()
          .then(data => { if (isMountedRef.current) setKeyDates(data); })
          .catch(err => { console.error('[DashboardData] key dates:', err); })
          .finally(() => { if (isMountedRef.current) setKeyDatesLoading(false); }),
        markSettled: () => setKeyDatesLoading(false),
      },
      clientConcentration: {
        fromBundle: b => { setClientConcentration(b.clientConcentration ?? null); setClientConcLoading(false); },
        fetchOwn: () => fetchClientConcentration()
          .then(data => { if (isMountedRef.current) setClientConcentration(data); })
          .catch(err => { console.error('[DashboardData] client conc:', err); })
          .finally(() => { if (isMountedRef.current) setClientConcLoading(false); }),
        markSettled: () => setClientConcLoading(false),
      },
      revenueCostBurnByProject: {
        fromBundle: b => { setRevenueCostBurnByProject(b.revenueCostBurnByProject ?? []); setRcbByProjectLoading(false); },
        fetchOwn: () => fetchRevenueCostBurnByProject()
          .then(data => { if (isMountedRef.current) setRevenueCostBurnByProject(data); })
          .catch(err => { console.error('[DashboardData] rcb by project:', err); })
          .finally(() => { if (isMountedRef.current) setRcbByProjectLoading(false); }),
        markSettled: () => setRcbByProjectLoading(false),
      },
      utilisationSummary: {
        fromBundle: b => { setUtilisationSummary(b.utilisationSummary ?? null); setUtilisationLoading(false); },
        fetchOwn: () => fetchUtilisationSummary()
          .then(val => { if (isMountedRef.current) setUtilisationSummary(val); })
          .catch(err => {
            if (!isMountedRef.current) return;
            console.error('[DashboardData] Failed to fetch utilisation summary:', err);
            setUtilisationSummary(null);
          })
          .finally(() => { if (isMountedRef.current) setUtilisationLoading(false); }),
        markSettled: () => setUtilisationLoading(false),
      },
      wipSummary: {
        // An independent section: the bundle does not carry it, so only fetchOwn runs.
        fromBundle: () => {},
        fetchOwn: () => fetchDashboardWipSummary(wipParams)
          .then(val => { if (isMountedRef.current) setWipSummary(val); })
          .catch(err => {
            if (!isMountedRef.current) return;
            console.error('[DashboardData] Failed to fetch WIP summary:', err);
            setWipSummary(null);
          })
          .finally(() => { if (isMountedRef.current) setWipLoading(false); }),
        markSettled: () => setWipLoading(false),
      },
    };
  };

  const fetchAll = async (isInitial: boolean) => {
    const mode = getDataMode();
    const isLive = mode === 'live';

    if (isInitial) {
      setKpisLoading(true);
      setFinKpisLoading(true);
      setAtRiskLoading(true);
      setProfitabilityLoading(true);
      setRevenueRecLoading(true);
      setKeyDatesLoading(true);
      setClientConcLoading(true);
      setRcbByProjectLoading(true);
      setBillingPipelineLoading(true);
      setWipLoading(true);
      setUtilisationLoading(true);
      setProjectsDataLoading(true);
    } else {
      setIsRefreshing(true);
    }
    setError(null);

    const promises: Promise<void>[] = [];

    if (isLive) {
      // ── Live mode: bundled and independent calls for the persona's widgets ─
      // Bundle sections share one GET dashboard/overview working set. Independent sections
      // and sections the server could not produce use their own endpoints.
      const sections = PERSONA_SECTIONS[persona];
      const loaders = buildSectionLoaders();

      // A widget this persona never renders must not hold its spinner up.
      for (const section of ALL_SECTIONS) {
        if (!sections.includes(section) && isMountedRef.current) loaders[section].markSettled();
      }

      const bundleSections = sections.filter(s => !INDEPENDENT_SECTIONS.has(s));
      const ownSections = sections.filter(s => INDEPENDENT_SECTIONS.has(s));

      for (const section of ownSections) promises.push(loaders[section].fetchOwn());

      // The billing pipeline is independent of the overview bundle, so both requests can
      // start together and neither delays the other's first paint.
      if (persona === 'executive') {
        promises.push(
          fetchBillingPipeline()
            .then(data => { if (isMountedRef.current) setBillingPipeline(data); })
            .catch(err => { console.error('[DashboardData] billing pipeline:', err); })
            .finally(() => { if (isMountedRef.current) setBillingPipelineLoading(false); }),
        );
      } else if (isMountedRef.current) {
        setBillingPipelineLoading(false);
      }

      if (bundleSections.length > 0) {
        promises.push(
          fetchDashboardOverview({ persona, managerId: revenueRecManagerId })
            .then(bundle => {
              const missing: DashboardOverviewSection[] = [];
              for (const section of bundleSections) {
                if (bundle.sections.includes(section)) {
                  if (isMountedRef.current) loaders[section].fromBundle(bundle);
                } else {
                  missing.push(section);
                }
              }
              return Promise.all(missing.map(s => loaders[s].fetchOwn())).then(() => undefined);
            })
            .catch(err => {
              console.error('[DashboardData] overview bundle:', err);
              for (const section of bundleSections) loaders[section].markSettled();
            }),
        );
      }

      // Project financial summary is now fetched on-demand per page by the
      // usePagedFinancialSummary hook. Just mark loading as complete.
      if (isMountedRef.current) setProjectsDataLoading(false);
    } else {
      // ── Demo/tour mode: keep the old fetchProjects path for mock data ──
      promises.push(
        fetchProjects('dashboard', undefined, { statusId: 2 })
          .then(val => {
            if (!isMountedRef.current) return;
            const allProjects: Project[] = Array.isArray(val) ? val : (val?.data ?? []);
            setProjects(allProjects);
            setLegacyRevenueRecognitions(deriveRevenueRecognitions(allProjects));
          })
          .catch(err => {
            if (!isMountedRef.current) return;
            console.error('[DashboardData] Failed to fetch projects:', err);
            setProjects([]);
            setLegacyRevenueRecognitions([]);
          })
          .finally(() => {
            if (!isMountedRef.current) return;
            setKpisLoading(false);
            setFinKpisLoading(false);
            setAtRiskLoading(false);
            setProfitabilityLoading(false);
            setRevenueRecLoading(false);
            setKeyDatesLoading(false);
            setClientConcLoading(false);
            setRcbByProjectLoading(false);
            setBillingPipelineLoading(false);
            setProjectsDataLoading(false);
          }),
      );
    }

    if (!isLive) {
      promises.push(
        fetchAllWIPReports('dashboard')
          .then(val => {
            if (!isMountedRef.current) return;
            setWipReports(val ?? []);
          })
          .catch(err => {
            if (!isMountedRef.current) return;
            console.error('[DashboardData] Failed to fetch WIP reports:', err);
            setWipReports([]);
          })
          .finally(() => {
            if (isMountedRef.current) setWipLoading(false);
          }),
      );

      promises.push(
        fetchUtilisationData('dashboard')
          .then(val => {
            if (!isMountedRef.current) return;
            setUtilisationData(val ?? []);
          })
          .catch(err => {
            if (!isMountedRef.current) return;
            console.error('[DashboardData] Failed to fetch utilisation:', err);
            setUtilisationData([]);
          })
          .finally(() => {
            if (isMountedRef.current) setUtilisationLoading(false);
          }),
      );
    }

    try {
      await Promise.allSettled(promises);
    } catch (err) {
      if (!isMountedRef.current) return;
      const message = err instanceof Error ? err.message : 'Failed to load dashboard data';
      console.error('[DashboardData] Unexpected error:', err);
      setError(message);
    }

    if (isMountedRef.current) {
      setLastFetchedAt(Date.now());
      setIsRefreshing(false);
    }
  };

  // Fetch once a company is selected and whenever it changes.
  // Also re-fetch when revenueRecManagerId changes (persona/user change). The bundle is
  // scoped by persona, so wait for the permissions the persona is derived from.
  useEffect(() => {
    if (!currentCompany?.id || permissionsLoading) return;
    void fetchAll(true);
  }, [currentCompany?.id, revenueRecManagerId, permissionsLoading, persona]);

  const refresh = async () => {
    await fetchAll(false);
  };

  // Page/band toggles and the threshold-mismatch effect can overlap; only the
  // most recently requested page may write to state, and the spinner stays up
  // until every overlapping request has settled.
  const wipRequestSeqRef = useRef(0);
  const wipRequestsInFlightRef = useRef(0);

  const loadWipPage = useCallback(async (
    page: number,
    band: WipAgeBand | null,
    thresholds?: DashboardWipThresholds,
  ) => {
    if (getDataMode() !== 'live') return;
    const seq = ++wipRequestSeqRef.current;
    wipRequestsInFlightRef.current += 1;
    setWipPageLoading(true);
    try {
      const val = await fetchDashboardWipSummary({
        page,
        pageSize: DASHBOARD_WIP_PAGE_SIZE,
        band,
        rowLimit: DASHBOARD_WIP_ROW_LIMIT,
        managerId: revenueRecManagerId,
        underBillingThreshold: thresholds?.underBillingThreshold,
        agedWipDaysThreshold: thresholds?.agedWipDaysThreshold ?? getWipThresholds().agedWipDaysThreshold,
      });
      if (isMountedRef.current && seq === wipRequestSeqRef.current) setWipSummary(val);
    } catch (err) {
      console.error('[DashboardData] Failed to page WIP summary:', err);
    } finally {
      wipRequestsInFlightRef.current -= 1;
      if (isMountedRef.current && wipRequestsInFlightRef.current === 0) setWipPageLoading(false);
    }
  }, [revenueRecManagerId]);

  // Derived: active projects (demo/tour mode backward compat)
  const activeProjects = projects.filter(p => p.statusId === 2);

  // Composite loading flags — projectsLoading is aliased for backward compat
  const projectsLoading = kpisLoading;
  const isLoading = kpisLoading || wipLoading || utilisationLoading;

  return (
    <DashboardDataContext.Provider
      value={{
        execKpis,
        finKpis,
        atRiskSummary,
        revenueRecognitionSummary,
        profitability,
        keyDates,
        clientConcentration,
        revenueCostBurnByProject,
        billingPipeline,
        projectFinancialSummary,
        wipSummary,
        loadWipPage,
        wipPageLoading,
        utilisationSummary,
        wipReports,
        utilisationData,
        projects,
        activeProjects,
        legacyRevenueRecognitions,
        kpisLoading,
        finKpisLoading,
        atRiskLoading,
        profitabilityLoading,
        revenueRecLoading,
        keyDatesLoading,
        clientConcLoading,
        rcbByProjectLoading,
        billingPipelineLoading,
        wipLoading,
        utilisationLoading,
        projectsDataLoading,
        projectsLoading,
        isLoading,
        isRefreshing,
        error,
        refresh,
        lastFetchedAt,
      }}
    >
      {children}
    </DashboardDataContext.Provider>
  );
}

// ── Consumer hook ─────────────────────────────────────────────────────────────
export function useDashboardData(): DashboardDataContextValue {
  const ctx = useContext(DashboardDataContext);
  if (!ctx) {
    throw new Error('useDashboardData must be used within a DashboardDataProvider');
  }
  return ctx;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Derive per-project RevenueRecognition from budget mock data (demo mode) or
 * project fields (live mode).  Kept for demo/tour backward compatibility.
 */
function deriveRevenueRecognitions(projects: Project[]): ProjectRevenueRecognition[] {
  const mode = getDataMode();

  return projects.map(project => {
    if (mode === 'demo' || mode === 'tour') {
      const budgetData = getBudgetDataByProjectId(project.id);
      const rr = budgetData?.salesBudget?.revenueRecognition;

      if (rr) {
        return {
          projectId: project.id,
          projectName: project.name,
          clientName: project.clientNames ?? 'Unknown',
          method: rr.method,
          budgetRevenue: project.budgetRevenue ?? 0,
          completionPercent: rr.percentComplete ?? project.completionPercent ?? 0,
          revenueRecognised: rr.revenueRecognised ?? rr.recognised ?? 0,
          revenueToRecognise: rr.revenueToRecognise ?? rr.toRecognise ?? 0,
          deferredRevenue: rr.deferredRevenue ?? rr.deferred ?? 0,
          unbilledRevenue: rr.unbilledRevenue ?? rr.unbilled ?? 0,
        };
      }
    }

    const completionPct = project.completionPercent ?? 0;
    const budgetRevenue = project.budgetRevenue ?? 0;
    const actualRevenue = project.actualRevenue ?? 0;
    const revenueRecognised = actualRevenue;
    const revenueToRecognise = Math.max(0, (completionPct / 100) * budgetRevenue - actualRevenue);
    const deferredRevenue = Math.max(0, actualRevenue - (completionPct / 100) * budgetRevenue);
    const unbilledRevenue = project.wipAmount ?? 0;

    return {
      projectId: project.id,
      projectName: project.name,
      clientName: project.clientNames ?? 'Unknown',
      method: (project.revenueRecognitionMethod ?? 'percentage-of-completion') as RevenueRecognition['method'],
      budgetRevenue,
      completionPercent: completionPct,
      revenueRecognised,
      revenueToRecognise,
      deferredRevenue,
      unbilledRevenue,
    };
  });
}
